import {
  infiniteQueryOptions,
  mutationOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { TrashEntry, TrashStructure } from '@/api/types'

interface TrashPage {
  count: number
  next: string | null
  previous: string | null
  results: TrashEntry[]
}

/** Which workspaces and applications have anything in the trash. */
export function trashStructureQuery() {
  return queryOptions({
    queryKey: qk.trashStructure(),
    queryFn: (): Promise<TrashStructure> => unwrap(getApi().GET('/api/trash/')),
  })
}

export function useTrashStructure() {
  return useQuery(trashStructureQuery())
}

/**
 * What is in one workspace's trash, newest first.
 *
 * Paged by page number rather than offset -- this endpoint counts in pages while
 * the notification one counts in rows, and the difference is the server's, not a
 * choice available here.
 */
export function trashContentsQuery(workspaceId: number, applicationId: number | null) {
  return infiniteQueryOptions({
    queryKey: qk.trashContents(workspaceId, applicationId),
    queryFn: ({ pageParam }): Promise<TrashPage> =>
      unwrap(
        getApi().GET('/api/trash/workspace/{workspace_id}/', {
          params: {
            path: { workspace_id: workspaceId },
            query:
              applicationId === null
                ? { page: pageParam }
                : { page: pageParam, application_id: applicationId },
          },
        }),
      ) as Promise<TrashPage>,
    initialPageParam: 1,
    getNextPageParam: (last, _pages, lastPage) =>
      last.next === null ? undefined : lastPage + 1,
  })
}

export function useTrashContents(workspaceId: number, applicationId: number | null) {
  return useInfiniteQuery(trashContentsQuery(workspaceId, applicationId))
}

/**
 * Puts one thing back.
 *
 * Everything is invalidated afterwards, for the reason an undo is: the entry
 * names a type and an id, and turning those into cache keys is a mapping that
 * would have to grow with every trashable type. Restoring is rare, and
 * `invalidateQueries` refetches only what something is subscribed to.
 *
 * It is not optional either -- the restore broadcasts to everyone *except* the
 * tab that asked for it, so nothing is coming to say what came back.
 */
export function restoreFromTrashMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (entry: TrashEntry) =>
      unwrap(
        getApi().PATCH('/api/trash/restore/', {
          body: {
            trash_item_type: entry.trash_item_type as 'workspace',
            trash_item_id: entry.trash_item_id,
            ...(entry.parent_trash_item_id !== undefined &&
            entry.parent_trash_item_id !== null
              ? { parent_trash_item_id: entry.parent_trash_item_id }
              : {}),
          },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.all })
    },
  })
}

export function useRestoreFromTrash() {
  return useMutation(restoreFromTrashMutation(useQueryClient()))
}

/** Deletes for good, either a whole workspace's trash or one application's. */
export function emptyTrashMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: ({
      workspaceId,
      applicationId,
    }: {
      workspaceId: number
      applicationId: number | null
    }) =>
      unwrap(
        getApi().DELETE('/api/trash/workspace/{workspace_id}/', {
          params: {
            path: { workspace_id: workspaceId },
            query: applicationId === null ? {} : { application_id: applicationId },
          },
        }),
      ),
    // Only the trash: emptying it destroys things that were already gone from
    // everywhere else, so nothing outside these two keys can have changed.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.trashStructure() })
    },
  })
}

export function useEmptyTrash() {
  return useMutation(emptyTrashMutation(useQueryClient()))
}
